import Accessor from '@arcgis/core/core/Accessor';
import { property, subclass } from '@arcgis/core/core/accessorSupport/decorators';
import { whenDefinedOnce } from '@arcgis/core/core/watchUtils';
import IdentityManager from "@arcgis/core/identity/IdentityManager";
import { getBaseUrl } from '../../utils/utils';
import { AppTypeInfo, EResourceType, ResourceInfo } from '../../widgets/AppTypeTile';
import { EAppTemplateType } from 'templates-common-library/baseClasses/CompatibilityChecker';
import request from "@arcgis/core/request";
import PortalItem from '@arcgis/core/portal/PortalItem';
import NavBarViewModel from 'src/app/ui/NavBar/NavBarViewModel';
import Telemetry from "@esri/telemetry";
import MyAppsViewModel from '../MyApps/MyAppsViewModel';
import AppT9N from "../../../t9n/App/App.json";

export type RedirectOption = "goToConfigure" | "goToItemDetails";

export interface PublishInfo {
  redirect: RedirectOption;
  title: string,
  folderId: string,
  tags: string,
  resourceInfo: ResourceInfo,
  appTypeInfo: AppTypeInfo;
}

export interface CopyInfo {
  newTitle: string;
  newTags: string;
  folder: string;
  appToCopyId: string;
  appTypeInfo: AppTypeInfo;
}

export interface UserFolder {
  id: string;
  title: string;
}

export enum EAppJSONTypeVals {
  webmap = "webmap",
  webscene = "webscene",
  group = "group"
}

export const AGOL_USER_ROOT_FOLDER_KEY = "root";

@subclass('Landing.data.PublishApp.PublishAppViewModel')
export default class PublishAppViewModel extends Accessor {

  @property()
  view: __esri.MapView | __esri.SceneView;

  @property()
  portal: __esri.Portal;

  @property()
  cred: __esri.Credential;

  @property()
  isPublishing: boolean = false;

  /** orgUrl with "http://"" replaced with "https://" */
  @property()
  orgUrl: string;

  /** portalUrl with "http://"" replaced with "https://" */
  @property()
  portalUrl: string;

  @property()
  folders: UserFolder[];

  @property({ readOnly: true })
  redirectionTarget: RedirectOption;

  @property()
  navbarVM: NavBarViewModel;

  @property()
  myAppsDataVM: MyAppsViewModel = new MyAppsViewModel();

  @property()
  telemetry: Telemetry;

  @property()
  appMessages: typeof AppT9N;

  private _baseUrl: string;
  private _loadingPageUrl: string;

  constructor(params?: any) {
    super(params);
    whenDefinedOnce(this, "appMessages", () => {
      this._defineLinkUrl();
    });

    whenDefinedOnce(this, "portal", async () => {
      await this.portal.load();
      this.orgUrl = getBaseUrl(this.portal);
      this.orgUrl = this.orgUrl.replace("http://", "https://");
      this.portalUrl = this.portal.url.replace("http://", "https://");

      // ::::: FOR DEVEXT ::::::::
      this._baseUrl = this.orgUrl;
      // ::::::::::::: FOR LOCALHOST TESTING :::::::::::::::
      // this._baseUrl = this.portalUrl;

      this.cred = await IdentityManager.checkSignInStatus(this.orgUrl + "/sharing");

      this.folders = await this._getUserFolders();
      this.folders.unshift({ id: AGOL_USER_ROOT_FOLDER_KEY, title: this.cred.userId });
    });
  }

  ///////////////////////////
  /// Public Functions
  ///////////////////////////

  async createApp(info: PublishInfo, telemetryFunc: Function): Promise<["success", string]> {
    try {
      const newTab = window.open(this._loadingPageUrl);

      telemetryFunc();
      this._set("redirectionTarget", info.redirect);

      this.isPublishing = true;
      const res = await this._addItemRestCall(info, this._decideValuesForPublish(info));
      if (res.success !== true) { throw new Error("Add Item Failed") }
      const appId = res.id;
      const updateRes = await this._updateItemRestCall(info.appTypeInfo, appId, info.folderId);
      if (updateRes.success !== true) { throw new Error("Update Failed") }
      this.isPublishing = false;

      newTab.location.href = info.redirect === "goToConfigure"
        ? `${this.orgUrl}/apps/configure-template/index.html?appid=${appId}`
        : `${this.orgUrl}/home/item.html?id=${appId}`;

      return ["success", appId];
    } catch (err) {
      throw new Error(err);
    }
  }

  async copyApp(info: CopyInfo, telemetryFunc: Function): Promise<["success", string]> {
    try {
      telemetryFunc();
      this.isPublishing = true;
      const res = await this._copyAppRestCall(info);
      if (res.success !== true) { throw new Error("Copy Failed") }
      const appId = res.itemId;
      const updateRes = await this._updateItemRestCall(info.appTypeInfo, appId, info.folder);
      if (updateRes.success !== true) { throw new Error("Update Failed") }
      this.isPublishing = false;
      return ["success", appId];
    } catch (err) {
      throw new Error(err);
    }
  }

  async createAppFromExisting(
    info: PublishInfo,
    existingAppId: string,
    telemetryFunc: Function
  ) {
    const newTab = window.open(this._loadingPageUrl);
    this._set("redirectionTarget", info.redirect);

    try {
      telemetryFunc();
      this.isPublishing = true;

      const existingApp = new PortalItem({ id: existingAppId, portal: this.portal });
      await existingApp.load();
      const existingAppData = await existingApp.fetchData();

      // Clear out type
      if (existingAppData?.values?.draft?.type != null) { existingAppData.values.draft.type = undefined; }
      if (existingAppData?.values?.type != null) { existingAppData.values.type = undefined; }
      // Clear out webmap
      if (existingAppData?.values?.draft?.webmap != null) { existingAppData.values.draft.webmap = undefined; }
      if (existingAppData?.values?.webmap != null) { existingAppData.values.webmap = undefined; }
      // Clear out webscene
      if (existingAppData?.values?.draft?.webscene != null) { existingAppData.values.draft.webscene = undefined; }
      if (existingAppData?.values?.webscene != null) { existingAppData.values.webscene = undefined; }
      // Clear out group
      if (existingAppData?.values?.draft?.group != null) { existingAppData.values.draft.group = undefined; }
      if (existingAppData?.values?.group != null) { existingAppData.values.group = undefined; }

      const { resourceItem, resourceType } = info?.resourceInfo;
      const id: string = resourceItem?.id;

      // Assign New Id to Existing App Data
      if (resourceType === EResourceType.Webmap) {
        existingAppData.values.webmap = id;
        existingAppData.values.type = EAppJSONTypeVals.webmap;
        if (existingAppData.values.draft) {
          existingAppData.values.draft.webmap = id;
          existingAppData.values.draft.type = EAppJSONTypeVals.webmap;
        }
      } else if (resourceType === EResourceType.Webscene) {
        existingAppData.values.webscene = id;
        existingAppData.values.type = EAppJSONTypeVals.webscene;
        if (existingAppData.values.draft) {
          existingAppData.values.draft.webscene = id;
          existingAppData.values.draft.type = EAppJSONTypeVals.webscene;
        }
      } else if (resourceType === EResourceType.Group) {
        existingAppData.values.group = id;
        existingAppData.values.type = EAppJSONTypeVals.group;
        if (existingAppData.values.draft) {
          existingAppData.values.draft.group = id;
          existingAppData.values.draft.type = EAppJSONTypeVals.group;
        }
      }

      const res = await this._addItemRestCall(info, existingAppData.values);
      if (res.success !== true) { throw new Error("create from existing Failed"); }
      const appId = res.id;
      const updateRes = await this._updateItemRestCall(info.appTypeInfo, appId, info.folderId);
      if (updateRes.success !== true) { throw new Error("Update Failed"); }

      this.isPublishing = false;
      newTab.location.href = info.redirect === "goToConfigure"
        ? `${this.orgUrl}/apps/configure-template/index.html?appid=${appId}`
        : `${this.orgUrl}/home/item.html?id=${appId}`;
      return "success";
    } catch (err) {
      throw new Error(err);
    }
  }

  async checkCanDelete(appId: string): Promise<{ itemId?: string, success: boolean }> {
    try {
      return this._canDeleteRestCall(appId);
    } catch (error) {
      console.error(error);
      return { success: false }
    }
  }

  async deleteItem(appId: string): Promise<boolean | string> {
    try {
      const res = this._deleteRestCall(appId);
      return res;
    } catch (error) {
      return error;
    }
  }

  async doesTitleExist(appTitle: string, folderId: string): Promise<boolean> {
    const requestUrl = `title: "${appTitle}" AND type:"Web Mapping Application" AND owner:"${this.cred.userId}" AND ownerfolder:"${folderId == null ? AGOL_USER_ROOT_FOLDER_KEY : folderId}"`;

    let searchResults: __esri.PortalItem[] = [];
    let start = 1;
    let query = {
      query: requestUrl,
      num: 100
    };

    while (start !== -1) {
      const res = await this.portal.queryItems(query);
      searchResults = searchResults.concat(res.results);
      start = res.nextQueryParams.start;
      query = res.nextQueryParams;
    }

    return searchResults.filter((item: __esri.PortalItem) => {
      return item.title === appTitle;
    })?.length > 0;
  }

  getFolderName(folderId: string): string {
    return this.folders?.find((folder: UserFolder) => {
      return folderId === folder.id;
    })?.title;
  }

  ///////////////////////////
  /// Private Functions
  ///////////////////////////

  private async _getUserFolders(): Promise<UserFolder[]> {
    const userFolderUrl = `${this._baseUrl}/sharing/rest/content/users/${this.cred.userId}?num=1&f=json`
    const res = await request(userFolderUrl);
    return res.data.folders;
  }

  private async _addItemRestCall(publishInfo: PublishInfo, values: any): Promise<{ "success": boolean, "id": string, "folder": string }> {
    const { userId } = this.cred;
    const { title, tags, appTypeInfo, folderId } = publishInfo;
    const { thumbnailUrl, appTypeUrlFragment, appTypeId } = appTypeInfo;

    const addItemInfo = {
      type: "Web Mapping Application",
      typeKeywords: "Web Map, Map, Online Map, Mapping Site, JavaScript, Ready To Use, configurableApp",
      thumbnailURL: thumbnailUrl,
      url: `${this.orgUrl}${appTypeUrlFragment}`,
      title: title,
      tags: tags,
      text: JSON.stringify({ "source": appTypeId, "folderId": folderId, "values": values }),
      f: "json"
    };

    if (folderId != null && folderId !== AGOL_USER_ROOT_FOLDER_KEY) {
      addItemInfo["folderId"] = folderId;
    }

    const res = await request(`${this._baseUrl}/sharing/rest/content/users/${userId}/${this._includeFolderId(folderId)}addItem`, {
      query: addItemInfo,
      method: "post"
    });

    return res.data;
  }

  /**
   * 2nd AGOL REST call to update newly created item's url
   * @param appId - appId returned from 1st REST call
   */
  private async _updateItemRestCall(appTypeInfo: AppTypeInfo, appId: string, folderId: string): Promise<{ "success": boolean, "id": string }> {

    const { userId } = this.cred;

    const updateItemInfo = {
      id: appId,
      url: `${this.orgUrl}${appTypeInfo.appTypeUrlFragment}?appid=${appId}`,
      f: "json"
    };

    if (folderId != null && folderId !== AGOL_USER_ROOT_FOLDER_KEY) {
      updateItemInfo["folderId"] = folderId;
    }

    const res = await request(`${this._baseUrl}/sharing/rest/content/users/${userId}/${this._includeFolderId(folderId)}items/${appId}/update`, {
      query: updateItemInfo,
      method: "post",
    });

    return res.data;
  }

  private _decideValuesForPublish(publishInfo: PublishInfo): any {
    const { resourceItem, resourceType } = publishInfo.resourceInfo;

    //////
    // NOTE: Keeping this around because that non-compat condition is important, and I think we'll need to reinstate it in the future
    //////
    // // For "no-resource" workflow, or if a non-compat exists
    // if (resourceType == EResourceType.None || publishInfo.appTypeInfo.compatibleMsgs != null) {
    //   return {};
    // }

    // For "no-resource" workflow
    if (resourceType == EResourceType.None) {
      return {};
    }

    // Special Cases for particular Templates
    const appType: EAppTemplateType = publishInfo.appTypeInfo.appTypeUrlFragment;
    if (appType === EAppTemplateType.Portfolio && resourceType !== EResourceType.Group) {
      const { id, title, itemUrl, description } = resourceItem as __esri.PortalItem;

      const finalItemUrl = this.orgUrl != null && id != null
        ? `${this.orgUrl}/sharing/rest/content/items/${id}`
        : itemUrl;

      const type = resourceType === EResourceType.Webmap
        ? "Web Map"
        : resourceType === EResourceType.Webscene
          ? "Web Scene"
          : null;
      return {
        itemCollection: [
          {
            addType: "itemBrowser",
            description,
            editorID: `${new Date().getTime()}`,
            id,
            title,
            type,
            url: finalItemUrl,
            visible: true
          }
        ]
      };
    }

    // Resource Type
    if (resourceType === EResourceType.Webmap) {
      return {
        webmap: resourceItem.id,
        type: EAppJSONTypeVals.webmap
      };
    } else if (resourceType === EResourceType.Webscene) {
      return {
        webscene: resourceItem.id,
        type: EAppJSONTypeVals.webscene
      };
    } else if (resourceType === EResourceType.Group) {
      return {
        group: resourceItem.id,
        type: EAppJSONTypeVals.group
      };
    } else { // any fall through cases then we don't know what to do with them, just return { }
      return {};
    }
  }

  private _includeFolderId(folderId: string): string {
    return folderId != null && folderId !== AGOL_USER_ROOT_FOLDER_KEY ? `${folderId}/` : "";
  }

  private async _copyAppRestCall(info: CopyInfo) {

    const { userId } = this.cred;
    const { newTitle, newTags, folder, appToCopyId } = info;

    const copyItemInfo = {
      title: newTitle,
      tags: newTags,
      folder: this._includeFolderId(folder).replace("/", ""),
      includeResources: true,
      copyPrivateResources: true,
      f: "json"
    };

    const res = await request(
      `${this._baseUrl}/sharing/rest/content/users/${userId}/items/${appToCopyId}/copy`, {
      query: copyItemInfo,
      method: "post",
    });

    return res.data;
  }

  private async _canDeleteRestCall(appId: string): Promise<{ itemId: string, success: boolean }> {
    const { userId } = this.cred;

    const info = {
      f: "json"
    };
    let res;
    try {
      res = await request(
        `${this._baseUrl}/sharing/rest/content/users/${userId}/items/${appId}/canDelete`, {
        query: info,
        method: "post",
      });
    } catch (err) {
      return { itemId: appId, success: false };
    }

    return res.data;
  }

  private async _deleteRestCall(appId: string): Promise<boolean | string> {
    const { userId } = this.cred;
    const info = {
      f: "json"
    };
    let res;
    try {
      res = await request(
        `${this._baseUrl}/sharing/rest/content/users/${userId}/items/${appId}/delete`, {
        query: info,
        method: "post",
      });
    } catch (err) {
      return err;
    }

    return res.data.success;
  }

  private _defineLinkUrl() {
    const { pathname } = window.location;
    const distPath = pathname.substring(0, pathname.lastIndexOf("/"));
    this._loadingPageUrl = `${distPath}/assets/images/loading.html?msg=${this?.appMessages?.afterCreateStr}`;
  }

}
